import { gql } from 'graphql-request';
import { graphqlClient } from '../client';
import { supabase } from '@/integrations/supabase/client';

// Define the create asset mutation
export const CREATE_ASSET_MUTATION = gql`
  mutation CreateAsset($input: AssetInput!) {
    createAsset(input: $input) {
      id
      collection_id
      type
      page_id
      slug
      version
      title
      description
      primary_original_url
      primary_cropped_url
      primary_markup_url
      secondary_original_url
      secondary_cropped_url
      secondary_markup_url
      secondary_version
      header
      subheader
      comparison_mode
      secondary_page_id
      primary_annotations
      secondary_annotations
      logo_url
      created_at
      updated_at
    }
  }
`;

// Define the delete asset mutation
export const DELETE_ASSET_MUTATION = gql`
  mutation DeleteAsset($id: ID!) {
    deleteAsset(id: $id) {
      id
      success
      message
    }
  }
`;

// Define input types
export interface AssetInput {
  collection_id: string;
  type: 'simple' | 'diff';
  page_id: string;
  slug: string;
  version: string;
  title: string;
  description?: string;
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  header?: string;
  subheader?: string;
  comparison_mode?: 'single' | 'side_by_side' | 'stacked';
  secondary_version?: string;
  secondary_page_id?: string;
  primary_annotations?: string;
  secondary_annotations?: string;
}

// Define response types
export interface Asset {
  id: string;
  collection_id: string;
  type: 'simple' | 'diff';
  page_id: string;
  slug: string;
  version: string;
  title: string;
  description?: string;
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  secondary_version?: string;
  header?: string;
  subheader?: string;
  comparison_mode?: 'single' | 'side_by_side' | 'stacked';
  secondary_page_id?: string;
  primary_annotations?: string;
  secondary_annotations?: string;
  logo_url?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAssetResponse {
  createAsset: Asset;
}

export interface DeleteAssetResponse {
  deleteAsset: {
    id: string;
    success: boolean;
    message: string;
  };
}

/**
 * Server-side Cloudinary upload logic helper
 * This will be used by the GraphQL resolver to handle image uploads
 */
export interface CloudinaryUploadResult {
  secure_url: string;
  public_id: string;
  version: number;
  format: string;
}

/**
 * Creates a new asset in a collection with server-side image handling
 * @param input The asset input data
 * @returns Promise with the created asset data
 */
export async function createAssetMutation(input: AssetInput): Promise<CreateAssetResponse> {
  try {
    console.log('🔄 [GraphQL] Creating asset:', {
      collection_id: input.collection_id,
      slug: input.slug,
      version: input.version,
      hasPrimaryOriginalUrl: !!input.primary_original_url,
      hasSecondaryOriginalUrl: !!input.secondary_original_url,
      isPrimaryCloudinary: input.primary_original_url?.includes('cloudinary') || false
    });

    // The server should store image URLs in the proper schema fields:
    // 1. primary_original_url: The original screenshot URL (Cloudinary or S3)
    // 2. primary_cropped_url: URL with crop transformations applied (if edited)
    // 3. primary_markup_url: URL with markup/annotations (if edited in Canva)
    // Note: Do NOT use primary_image field (marked as deprecated in schema)

    const response = await graphqlClient.request<CreateAssetResponse>(
      CREATE_ASSET_MUTATION,
      { input }
    );

    // Check if the response contains the created asset
    if (!response || !response.createAsset) {
      console.error('❌ [GraphQL] Server returned null response for createAsset');
      throw new Error('Asset creation failed: Server returned no data (likely due to database constraints)');
    }

    console.log('✅ [GraphQL] Asset created successfully:', {
      id: response.createAsset.id,
      hasPrimaryOriginalUrl: !!response.createAsset.primary_original_url,
      isCloudinary: response.createAsset.primary_original_url?.includes('cloudinary') || false
    });

    return response;
  } catch (error: any) {
    console.error('❌ [GraphQL] Error creating asset:', error);
    
    if (error.message?.includes('network')) {
      throw new Error('Network error: Could not reach the GraphQL server');
    } else if (error.message?.includes('Collection not found')) {
      throw new Error('Collection not found. Please select a valid collection.');
    } else if (error.message?.includes('GraphQL')) {
      throw error;
    } else {
      throw new Error(`Error creating asset: ${error.message || 'Unknown error'}`);
    }
  }
}

/**
 * Deletes an asset from a collection
 * @param id The asset ID to delete
 * @returns Promise with deletion result
 */
export async function deleteAssetMutation(id: string): Promise<DeleteAssetResponse> {
  try {
    console.log(`🔄 [GraphQL] Deleting asset ${id}`);

    const response = await graphqlClient.request<DeleteAssetResponse>(
      DELETE_ASSET_MUTATION,
      { id }
    );

    console.log('✅ [GraphQL] Asset deleted successfully:', response.deleteAsset);
    return response;
  } catch (error: any) {
    console.error('❌ [GraphQL] Error deleting asset:', error);
    
    if (error.message?.includes('network')) {
      throw new Error('Network error: Could not reach the GraphQL server');
    } else if (error.message?.includes('Asset not found')) {
      throw new Error('Asset not found. It may have already been deleted.');
    } else if (error.message?.includes('GraphQL')) {
      throw error;
    } else {
      throw new Error(`Error deleting asset: ${error.message || 'Unknown error'}`);
    }
  }
}

/**
 * Server-side implementation notes for the GraphQL resolver:
 *
 * The createAsset resolver should store image URLs according to the schema:
 *
 * 1. Image URL Fields (as per @documentation/app_assets_schema.md):
 *    - primary_original_url: Original screenshot URL
 *    - primary_cropped_url: URL with crop transformations
 *    - primary_markup_url: URL with markup/annotations
 *    - DO NOT USE: primary_image (deprecated field)
 *
 * 2. The resolver should accept these URLs from the client and store them
 *    in the appropriate database columns without modification
 *
 * 3. Type validation:
 *    - type: 'simple' | 'diff'
 *    - comparison_mode: 'single' | 'side_by_side' | 'stacked'
 */

/**
 * Direct database insert for creating assets (bypasses GraphQL)
 * Use this when you have all required fields and want faster, more reliable inserts
 */
export interface CreateAssetDirectInput {
  collection_id: string;
  type: 'simple' | 'diff';
  page_id: string;
  slug: string;
  version: string;
  title?: string;
  description?: string;
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  comparison_mode?: 'single' | 'side_by_side' | 'stacked';
  secondary_version?: string;
  secondary_page_id?: string;
  header?: string;
  subheader?: string;
  primary_annotations?: unknown;
  secondary_annotations?: unknown;
  crop_coordinates?: unknown;
  crop_aspect_ratio?: string;
  primary_marker_x?: number;
  primary_marker_y?: number;
  secondary_marker_x?: number;
  secondary_marker_y?: number;
}

/**
 * Creates an asset directly in the database (bypasses GraphQL edge function)
 * This is more reliable and provides better error messages
 */
export async function createAssetDirectDB(input: CreateAssetDirectInput): Promise<{ id: string }> {
  try {
    console.log('🔄 [Direct DB] Creating asset:', {
      collection_id: input.collection_id,
      slug: input.slug,
      version: input.version,
      page_id: input.page_id,
      type: input.type,
    });

    const { data, error } = await supabase
      .from('app_assets')
      .insert({
        collection_id: input.collection_id,
        type: input.type,
        page_id: input.page_id,
        slug: input.slug,
        version: input.version,
        title: input.title || '',
        description: input.description || '',
        primary_original_url: input.primary_original_url,
        primary_cropped_url: input.primary_cropped_url,
        primary_markup_url: input.primary_markup_url,
        secondary_original_url: input.secondary_original_url,
        secondary_cropped_url: input.secondary_cropped_url,
        secondary_markup_url: input.secondary_markup_url,
        comparison_mode: input.comparison_mode || 'side_by_side',
        secondary_version: input.secondary_version,
        secondary_page_id: input.secondary_page_id,
        header: input.header,
        subheader: input.subheader,
        primary_annotations: input.primary_annotations,
        secondary_annotations: input.secondary_annotations,
        crop_coordinates: input.crop_coordinates,
        crop_aspect_ratio: input.crop_aspect_ratio,
        primary_marker_x: input.primary_marker_x,
        primary_marker_y: input.primary_marker_y,
        secondary_marker_x: input.secondary_marker_x,
        secondary_marker_y: input.secondary_marker_y,
      })
      .select('id')
      .single();

    if (error) {
      console.error('❌ [Direct DB] Error creating asset:', error);
      throw new Error(`Failed to create asset: ${error.message}`);
    }

    if (!data) {
      throw new Error('Asset creation failed: No data returned');
    }

    console.log('✅ [Direct DB] Asset created successfully:', data.id);
    return { id: data.id };
  } catch (error: any) {
    console.error('❌ [Direct DB] Error creating asset:', error);
    throw new Error(`Error creating asset: ${error.message || 'Unknown error'}`);
  }
}